"use client";

import { useEffect, useRef, useCallback } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useSessionContext } from "@/context";
import { routes } from "@/constants";
import { toast } from "sonner";
import { LoadingSpinner } from "@focal/ui-kit";
import { sendLog } from "@/api/client/logger";
import { LogLevel } from "@/types";
import { useIsMobileView } from "@/hooks/useIsMobileView";
import { useOnboardingStore } from "@/store/onboarding";

interface OAuthState {
  userid: string;
  returnToOnboarding?: boolean;
}

/**
 * Owns the onboarding redirect gates. It deliberately does NOT exchange an
 * OAuth authorization code: `app/oauth/[platform]/page.tsx` is the single
 * owner of that, and this component is mounted around it.
 *
 * Both used to post the code — this one whenever onboarding was incomplete,
 * the page whenever a `code` was in the URL — each with its own guard ref and
 * no knowledge of the other. Child effects run before parent effects, so one
 * return from the provider fired two `POST /callback/:platform` requests
 * microseconds apart, each minting its own Recall calendar for the same
 * account and duplicating every meeting (BE-453).
 */
export function OAuthOnboardingHandler({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { session, isReady } = useSessionContext();
  const isMobile = useIsMobileView();
  const hasProcessedOAuth = useRef(false);
  const setOnboardingError = useOnboardingStore((state) => state.setError);

  // Derived in render, not just in the effect: the gate below has to be able to
  // decide whether a redirect is pending *before* children are handed to React.
  const isOauthPage = pathname.startsWith("/oauth/");
  const isOnboardingPage = pathname === routes.onboarding.index;
  const onboardingCompleted = session?.trusted_metadata?.onboardingCompleted;

  /**
   * Handle a provider that came back with `?error=` instead of a code (denied
   * consent, missing admin approval). No exchange happens here — there is
   * nothing to exchange — so returning the user to onboarding with an
   * explanation is the whole job.
   */
  const handleOAuthRedirectError = useCallback(() => {
    if (hasProcessedOAuth.current) return;

    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");
    const stateParam = searchParams.get("state");
    const platform = pathname.split("/").pop(); // Extract platform from path

    let state: OAuthState | null = null;
    if (stateParam) {
      try {
        state = JSON.parse(stateParam) as OAuthState;
      } catch (e) {
        console.error("Failed to parse state parameter:", e);
      }
    }

    // Check if this is an onboarding OAuth flow
    const onboardingCompleted = session?.trusted_metadata?.onboardingCompleted;
    const isOnboardingFlow = state?.returnToOnboarding || onboardingCompleted === false;

    // Handle OAuth error in URL (user denied access or other OAuth errors)
    if (error && isOnboardingFlow) {
      hasProcessedOAuth.current = true;

      sendLog({
        level: LogLevel.ERROR,
        message: "OAuth onboarding redirect error",
        component: "OAuthOnboardingHandler",
        error: error,
        data: {
          errorDescription: errorDescription || "",
          platform: platform || "",
        },
      });

      const isAccessDenied = error === "access_denied";
      const isPermissionsError =
        isAccessDenied ||
        error.includes("consent") ||
        error.includes("unauthorized");

      const userFriendlyMessage = isPermissionsError
        ? "Could not connect your account. Please contact your IT admin for permissions."
        : `Error. Could not connect your account${errorDescription ? `: ${errorDescription}` : "."}`;

      toast.error(userFriendlyMessage, {
        duration: 5000,
      });

      // Same reason the callback page stores its failure: this toast is gone by
      // the time the user is looking at the step they have to re-run, and a
      // denied consent is recoverable once the admin grants it. Without this the
      // provider-error path lands on onboarding with nothing shown at all.
      setOnboardingError({
        message: userFriendlyMessage,
        type: isPermissionsError ? "permissions" : "unknown",
        platform:
          platform === "microsoft" || platform === "google"
            ? platform
            : undefined,
        timestamp: Date.now(),
      });

      // Redirect to onboarding immediately
      router.push(routes.onboarding.index);
      return;
    }

    // A code, not an error: `app/oauth/[platform]/page.tsx` exchanges it and
    // returns to onboarding itself. Posting it here too is what created two
    // calendars per connect.
  }, [pathname, searchParams, session, router, setOnboardingError]);

  useEffect(() => {
    if (!isReady) return;

    // Mobile users bypass onboarding entirely without flipping onboardingCompleted,
    // so a later desktop sign-in still gets the real onboarding flow.
    if (isMobile) {
      if (isOnboardingPage) {
        router.push(routes.inPersonMobile.index);
      }
      return;
    }

    // Every OAuth page, not just one where the session already says onboarding
    // is incomplete: a callback can carry `returnToOnboarding` while
    // `onboardingCompleted` reads true or undefined, and that denied-consent
    // flow still belongs back in onboarding. The internal onboarding check is
    // what decides, so this is a no-op for an ordinary connect.
    //
    // Note there is no redirect here — the page has to stay mounted to finish
    // the exchange, and it navigates to onboarding itself.
    if (isOauthPage) {
      handleOAuthRedirectError();
    }

    // If onboarding is not complete (false) and user is not on onboarding page, redirect to onboarding
    if (
      onboardingCompleted === false &&
      !isOnboardingPage &&
      !isOauthPage &&
      session
    ) {
      router.push(routes.onboarding.index);
    }

    // If onboarding is complete or undefined and user is on onboarding page, redirect to home
    if (
      (onboardingCompleted === true || onboardingCompleted === undefined) &&
      isOnboardingPage
    ) {
      router.push(routes.home.index);
    }
  }, [
    isReady,
    session,
    pathname,
    router,
    handleOAuthRedirectError,
    isMobile,
    isOauthPage,
    isOnboardingPage,
    onboardingCompleted,
  ]);

  // The effect above runs *after* children render, so without this the user is
  // handed a live app on a route they are about to be moved off. On a slow boot
  // that window is not a flicker: production telemetry caught a first-time user
  // sitting on a fully painted /home for 73 seconds before onboarding appeared,
  // clicking navigation that was about to be torn out from under her. A click
  // in that window does nothing and reports nothing.
  //
  // OAuth pages are deliberately exempt: that page has to stay mounted to
  // finish exchanging its code (see the doc comment above).
  const redirectPending = isMobile
    ? isOnboardingPage
    : (onboardingCompleted === false &&
        !isOnboardingPage &&
        !isOauthPage &&
        !!session) ||
      ((onboardingCompleted === true || onboardingCompleted === undefined) &&
        isOnboardingPage);

  if (isReady && redirectPending) {
    return (
      <div className="flex flex-1 h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return <>{children}</>;
}
